import {
  connectConnectorCatalogItemWithApiKey,
  connectConnectorCatalogItemWithCredentials,
  getConnectorCatalogConnectUrl,
} from "@/components/connectors/api.ts";
import { CustomMcpDialog } from "@/components/connectors/CustomMcpDialog.tsx";
import { ImapConnectButton } from "@/components/connectors/ImapConnectDialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { isStepUpGatedPath } from "@/lib/stepUp.ts";
import { useStepUp } from "@/lib/useStepUp.ts";
import { cn } from "@/lib/utils.ts";
import { connectorConnectMode } from "@/shared/connectors/connectMode.ts";
import type { ConnectorCatalogItem } from "@/types/connectors.ts";
import { reconnectButtonClass } from "@/components/connectors/ConnectorTile.tsx";
import { markConnectStarted } from "@/components/connectors/pendingConnect.ts";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

/**
 * The Connect / Reconnect button for one catalog connector.
 *
 * Shared by the Settings gallery and the in-thread connector card, which is
 * what lets a connector needing a typed key be connected from a conversation:
 * the dialog posts the secret straight to the connector endpoint, exactly as
 * Settings does, so it never becomes a message.
 */
export const ConnectorConnectAction = ({
  item,
  statsigName,
}: {
  item: ConnectorCatalogItem;
  statsigName: string;
}) => {
  const queryClient = useQueryClient();
  const connectUrl = getConnectorCatalogConnectUrl(item);
  const stepUp = useStepUp();
  // A Google or Microsoft connect from an account proven only by a claim link
  // would open a tab that answers OTP_REQUIRED; prove the number here first.
  const stepUpFirst = stepUp.required && connectUrl !== null && isStepUpGatedPath(connectUrl);
  const { mode, label, hidden, needsReconnect } = connectorConnectMode(item);
  const isSimpleSecret = mode === "apiKey";
  const isBearerToken = item.credentialKind === "bearer_token";
  const [dialogOpen, setDialogOpen] = useState(false);

  // No success toast on either mutation: the tile flipping to "Connected" is
  // the confirmation. Failures keep their toasts - a rejected credential has
  // no in-place signal.
  //
  // Both invalidate `currentUser` alongside the connector keys: it carries the
  // `needsAuth` flags behind the app-wide reauth banner, which the server has
  // just cleared, and without it the banner keeps asking for a reconnect that
  // already succeeded.
  const simpleSecretMutation = useMutation({
    mutationFn: (secret: string) => connectConnectorCatalogItemWithApiKey(item, secret),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectorCatalog"] });
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      setDialogOpen(false);
    },
    onError: () =>
      errorToast(
        isBearerToken
          ? "Couldn't connect — that Bearer token was rejected."
          : "Couldn't connect — that API key was rejected.",
      ),
  });

  const credentialMutation = useMutation({
    mutationFn: (credentials: Record<string, unknown>) =>
      connectConnectorCatalogItemWithCredentials(item, credentials),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connectorCatalog"] });
      queryClient.invalidateQueries({ queryKey: ["connectors"] });
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      setDialogOpen(false);
    },
    onError: () => errorToast("Couldn't connect with that credential JSON."),
  });

  const form = useForm({
    defaultValues: { credentialInput: isSimpleSecret ? "" : "{}" },
    onSubmit: ({ value }) => {
      if (isSimpleSecret) {
        simpleSecretMutation.mutate(value.credentialInput.trim());
        return;
      }
      try {
        const parsed: unknown = JSON.parse(value.credentialInput || "{}");
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          throw new Error("credentials must be an object");
        }
        credentialMutation.mutate(parsed as Record<string, unknown>);
      } catch {
        errorToast("Credentials must be a JSON object.");
      }
    },
  });

  if (hidden) return null;

  const reconnectClasses = needsReconnect ? reconnectButtonClass : "";

  if (mode === "oauth" && connectUrl) {
    // Per-account cards own the connected signal, so a connected provider's
    // tile always offers a plain "Connect", sized to the badges (h-6, text-xs)
    // so buttons and badges read as one row of chips. The accessible name
    // stays specific for connected items, where the visible label alone reads
    // ambiguously beside a Connected badge.
    return (
      <Button
        variant="primary"
        size="xs"
        asChild
        className={cn("h-6 px-2 text-xs shrink-0", reconnectClasses)}
        aria-label={
          item.connected && !needsReconnect ? `Connect another ${item.name} account` : undefined
        }
        data-statsig-name={statsigName}
      >
        {stepUpFirst ? (
          <a href={stepUp.href}>{label}</a>
        ) : (
          <a
            href={connectUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              if (item.provider) markConnectStarted(item.provider);
            }}
          >
            {label}
          </a>
        )}
      </Button>
    );
  }

  // Address + password + server settings: more than the single-secret dialog
  // collects. A tile standing for one provider passes its preset, which swaps
  // detection-by-address for that provider's setup steps.
  if (mode === "imapCredentials" && item.credentialSubmitPath) {
    return (
      <ImapConnectButton
        submitPath={item.credentialSubmitPath}
        label={label}
        className={cn("h-6 px-2 text-xs shrink-0", reconnectClasses)}
        statsigName={statsigName}
        preset={item.imapSetup ? { ...item.imapSetup, name: item.name } : undefined}
      />
    );
  }

  // The entry tile for a user's own MCP servers. There is no server to connect
  // yet: Add opens the flow that registers one from its URL.
  if (mode === "customMcp") {
    return (
      <>
        <Button
          variant="primary"
          size="xs"
          className="h-6 px-2 text-xs shrink-0"
          onClick={() => setDialogOpen(true)}
          data-statsig-name={statsigName}
        >
          {label}
        </Button>
        <CustomMcpDialog open={dialogOpen} onOpenChange={setDialogOpen} target={{ kind: "add" }} />
      </>
    );
  }

  // Either an OAuth item the server gave no connect path, or a credential item
  // with no submit path. Nowhere to send the user, so offer nothing.
  if (mode === "unsupported") return null;

  const mutationPending = isSimpleSecret
    ? simpleSecretMutation.isPending
    : credentialMutation.isPending;

  return (
    <>
      <Button
        variant="primary"
        size="xs"
        className={cn("h-6 px-2 text-xs shrink-0", reconnectClasses)}
        onClick={() => setDialogOpen(true)}
        data-statsig-name={statsigName}
      >
        {label}
      </Button>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect {item.name}</DialogTitle>
            <DialogDescription>
              {isSimpleSecret
                ? isBearerToken
                  ? "Paste your personal Bearer token. It's stored encrypted and sent only to this service."
                  : "Paste your personal API key. It's stored encrypted and sent only to this service."
                : "Paste the credential JSON configured by your administrator. It's stored encrypted and sent only to this API."}
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
            className="space-y-3"
          >
            <form.Field
              name="credentialInput"
              validators={{
                onChange: ({ value }) =>
                  isSimpleSecret && !value.trim()
                    ? isBearerToken
                      ? "A Bearer token is required"
                      : "An API key is required"
                    : undefined,
              }}
            >
              {(field) =>
                isSimpleSecret ? (
                  <Input
                    type="password"
                    placeholder={isBearerToken ? "Bearer token" : "API key"}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                ) : (
                  <Textarea
                    rows={8}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    className="font-mono text-xs"
                  />
                )
              }
            </form.Field>
            <DialogFooter>
              <Button
                type="submit"
                size="sm"
                disabled={mutationPending}
                data-statsig-name={
                  isSimpleSecret
                    ? "connectors-catalog-connect-key-submit"
                    : "connectors-catalog-connect-credentials-submit"
                }
              >
                {mutationPending ? "Connecting…" : "Connect"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
